import { useState, useCallback, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import PageLayout from "@/components/feature/PageLayout";
import SectionTitle from "@/components/base/SectionTitle";
import { useHashScroll } from "@/hooks/useHashScroll";

interface ProductData {
  id: string;
  name: string;
  category: string;
  desc: string;
  specs: string[];
  pdf: string;
  image: string;
  hoverImage?: string;
  badge?: string;
  subProducts?: { name: string; pdf: string }[];
}

const products: ProductData[] = [
  {
    id: "kg200",
    name: "ガス腐食試験機 KG200",
    category: "試験機器",
    desc: "接触機構部品を含んだ電子部品・材料の環境試験に最適。シングルガスとミックスガスの腐食試験が可能で、より自然環境に近い再現試験を実現します。5種の腐食性ガス（H2S、SO2、NO2、Cl2、O3）に対応。",
    specs: [
      "試験槽容量：200L",
      "対応ガス：H2S、SO2、NO2、Cl2、O3（単独・混合）",
      "温湿度制御：温度 +25〜+65℃ / 湿度 60〜95%RH",
      "オプション温湿度：80℃/95%RH、85℃/90%RH",
      "ガス濃度制御：ppm〜ppbレベル",
    ],
    pdf: "https://www.factk.jp/factk/pdf/kg200.pdf",
    image: "https://storage.readdy-site.link/project_files/46c769b0-62ed-4752-96b8-d59840c4cf62/774725c2-be14-45fd-a593-2aace2beeb1f_compressed_IMG_5485.webp",
    hoverImage: "https://public.readdy.ai/ai/img_res/edited_74b0672a597f173c480a71a0d30e0e23_9364fde3.jpg",
    subProducts: [
      { name: "KG200 製品カタログ", pdf: "https://www.factk.jp/factk/pdf/kg200.pdf" },
      { name: "KG200 試験槽内サイズ", pdf: "https://www.factk.jp/factk/pdf/kg200_chambersize.pdf" },
    ],
  },
  {
    id: "kg600",
    name: "ガス腐食試験機 KG600",
    category: "試験機器",
    desc: "大型試験槽を搭載したガス腐食試験機。大きな試験サンプルや複数部品の同時試験に対応。5種の腐食性ガス（H2S、SO2、NO2、Cl2、O3）の単独および混合ガス試験が可能です。",
    specs: [
      "試験槽容量：600L",
      "対応ガス：H2S、SO2、NO2、Cl2、O3（単独・混合）",
      "温湿度制御：温度 +25〜+65℃ / 湿度 60〜95%RH",
      "オプション温湿度：80℃/95%RH、85℃/90%RH",
      "ガス濃度制御：ppm〜ppbレベル",
    ],
    pdf: "https://www.factk.jp/factk/pdf/kg600.pdf",
    image: "https://storage.readdy-site.link/project_files/46c769b0-62ed-4752-96b8-d59840c4cf62/e0a8c883-b71c-43ea-9955-42f5947b97e4_compressed_IMG_5613-Copy.webp",
    hoverImage: "https://storage.readdy-site.link/project_files/46c769b0-62ed-4752-96b8-d59840c4cf62/93a85bab-2007-44fd-9077-2160951d1bc0_compressed_IMG_5589-Copy-4.webp",
    subProducts: [
      { name: "KG600 製品カタログ", pdf: "https://www.factk.jp/factk/pdf/kg600.pdf" },
      { name: "KG600 試験槽内サイズ", pdf: "https://www.factk.jp/factk/pdf/kg600_chambersize.pdf" },
    ],
  },
  {
    id: "ms7500",
    name: "接触抵抗評価装置 MS7500",
    category: "測定装置",
    desc: "接点材料の接触抵抗と接触力の関係を高精度に測定。板材・丸棒など殆どの接点材料における評価が可能で、環境試験後の評価も同時に行えます。説得力のある資料作成に最適です。",
    specs: [
      "測定方式：4端子法",
      "荷重範囲：0〜20g（0.1g分解能）",
      "測定機能：エリア分析・荷重変動・摺動評価・フレッティング",
      "プローブ：各種プローブオプション対応",
    ],
    pdf: "https://www.factk.jp/factk/pdf/ms7500catalog.pdf",
    image: "https://storage.readdy-site.link/project_files/46c769b0-62ed-4752-96b8-d59840c4cf62/f0ea9a7f-d24c-488e-8508-8d91430d10b0_compressed_IMG_5472-3.webp",
    subProducts: [
      { name: "MS7500 製品カタログ", pdf: "https://www.factk.jp/factk/pdf/ms7500catalog.pdf" },
      { name: "MS7500 オプション資料", pdf: "https://www.factk.jp/factk/pdf/ms7500option.pdf" },
      { name: "MS7500 プローブカタログ", pdf: "https://www.factk.jp/factk/pdf/ms7500probe.pdf" },
    ],
  },
  {
    id: "dryalfa",
    name: "ドライアルファ",
    category: "表面処理剤",
    desc: "高性能接触不良・酸化・雑音・硫化防止剤。最強の銀変色防止性能と低挿抜効果を実現するコンタクト用表面処理剤です。メッキ加工技術との併用でさらなる効果を発揮します。",
    specs: [
      "タイプ：接触用表面処理剤",
      "主な効果：銀変色防止、低挿抜、酸化防止",
      "用途：コネクタ・リレー・スイッチ接点",
      "特長：メッキ加工との併用可",
    ],
    pdf: "https://storage.readdy-site.link/project_files/46c769b0-62ed-4752-96b8-d59840c4cf62/cba5a155-f7f4-42c9-a6fc-e9d1774f0d5c_catalog_dryalpha.pdf",
    image: "https://storage.readdy-site.link/project_files/46c769b0-62ed-4752-96b8-d59840c4cf62/a8b54d4e-59b0-4c38-889d-70a915b54a27_compressed_IMG_5491-Copy.webp",
    subProducts: [
      { name: "ドライアルファ 製品カタログ", pdf: "https://storage.readdy-site.link/project_files/46c769b0-62ed-4752-96b8-d59840c4cf62/cba5a155-f7f4-42c9-a6fc-e9d1774f0d5c_catalog_dryalpha.pdf" },
    ],
  },
];

interface CatalogItem {
  label: string;
  href: string;
}

const kgCatalogs: CatalogItem[] = [
  { label: "KGシリーズ 製品価格表", href: "https://www.factk.jp/factk/pdf/kg_price2026.pdf" },
  { label: "KGシリーズ スペック比較", href: "https://www.factk.jp/factk/pdf/kg_spec.pdf" },
  { label: "ガス腐食試験機の性能", href: "https://www.factk.jp/factk/pdf/kg_perfom.pdf" },
  { label: "KGシリーズ 安全・省エネ", href: "https://www.factk.jp/factk/pdf/kg_safety.pdf" },
  { label: "KGシリーズ 操作性", href: "https://www.factk.jp/factk/pdf/kg_opera.pdf" },
  { label: "KGシリーズ メンテナンスのご案内", href: "https://www.factk.jp/factk/pdf/kg_mainte.pdf" },
  { label: "KGシリーズ 遠隔監視システム", href: "https://www.factk.jp/factk/pdf/kg_remote.pdf" },
  { label: "高温ガス腐食試験のご案内", href: "https://www.factk.jp/factk/pdf/htgas.pdf" },
];

const msCatalogs: CatalogItem[] = [
  { label: "コンタクト評価例", href: "https://www.factk.jp/factk/pdf/contactevaluation.pdf" },
  { label: "リレーコンタクト測定例", href: "https://www.factk.jp/factk/pdf/relaycontact.pdf" },
  { label: "接触不良解析例", href: "https://www.factk.jp/factk/pdf/contact%20failure_analysis.pdf" },
];

const generalCatalogs: CatalogItem[] = [
  { label: "総合カタログ", href: "https://www.factk.jp/factk/pdf/generalinformation.pdf" },
  { label: "会社案内", href: "https://www.factk.jp/factk/pdf/companyinfo.pdf" },
  { label: "受託試験のご案内", href: "https://www.factk.jp/factk/pdf/testinfo.pdf" },
];

interface Tab {
  id: string;
  label: string;
}

const tabs: Tab[] = [
  { id: "kg200", label: "KG200" },
  { id: "kg600", label: "KG600" },
  { id: "ms7500", label: "MS7500" },
  { id: "dryalfa", label: "ドライアルファ" },
  { id: "catalog", label: "資料カタログ" },
];

const TAB_BAR_HEIGHT = 52;
const NAVBAR_HEIGHT_DESKTOP = 80;
const NAVBAR_HEIGHT_MOBILE = 64;

function getScrollOffset(): number {
  const navbarH = window.innerWidth >= 768 ? NAVBAR_HEIGHT_DESKTOP : NAVBAR_HEIGHT_MOBILE;
  return navbarH + TAB_BAR_HEIGHT + 12;
}

export default function Products() {
  useHashScroll();
  const [activeTab, setActiveTab] = useState(tabs[0].id);
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});
  const isScrollingByClick = useRef(false);

  const scrollToSection = useCallback((tabId: string) => {
    const el = sectionRefs.current[tabId];
    if (!el) return;
    isScrollingByClick.current = true;
    setActiveTab(tabId);
    const top = el.getBoundingClientRect().top + window.scrollY - getScrollOffset();
    window.scrollTo({ top, behavior: "smooth" });
    setTimeout(() => {
      isScrollingByClick.current = false;
    }, 800);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (isScrollingByClick.current) return;
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length > 0) {
          const id = visible[0].target.id;
          if (id && tabs.some((t) => t.id === id)) {
            setActiveTab(id);
          }
        }
      },
      {
        rootMargin: `-${getScrollOffset()}px 0px -70% 0px`,
        threshold: 0,
      }
    );

    tabs.forEach((t) => {
      const el = sectionRefs.current[t.id];
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <PageLayout>
      {/* Hero */}
      <div className="relative py-14 md:py-20 bg-primary-700 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://public.readdy.ai/ai/img_res/edited_c223cce1a4d6486b7ef9a87de61bfc65_6d95594f.jpg"
            alt=""
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="relative z-10 w-full px-4 md:px-8">
          <div className="max-w-4xl mx-auto">
            <nav className="mb-4">
              <ol className="flex items-center gap-2 text-sm text-background-300">
                <li>
                  <Link to="/" className="hover:text-background-100 transition-colors">
                    ホーム
                  </Link>
                </li>
                <li>
                  <i className="ri-arrow-right-s-line text-xs" />
                </li>
                <li className="text-background-100">製品</li>
              </ol>
            </nav>
            <h1 className="text-3xl md:text-4xl font-black text-background-50">製品</h1>
            <p className="text-accent-300 text-sm mt-2">our products</p>
          </div>
        </div>
      </div>

      {/* Tab Navigation — sticky */}
      <div className="sticky z-40 bg-background-50 border-b border-background-200/70" style={{ top: window.innerWidth >= 768 ? NAVBAR_HEIGHT_DESKTOP : NAVBAR_HEIGHT_MOBILE }}>
        <div className="w-full px-4 md:px-8">
          <div className="max-w-5xl mx-auto flex items-center gap-1 overflow-x-auto scrollbar-hide py-1.5">
            {tabs.map((t) => (
              <button
                key={t.id}
                onClick={() => scrollToSection(t.id)}
                className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-semibold transition-all duration-200 cursor-pointer ${
                  activeTab === t.id
                    ? "bg-primary-500 text-background-50"
                    : "text-foreground-600 hover:text-foreground-900 hover:bg-background-100"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Product Sections */}
      <div className="w-full px-4 md:px-8 py-12 md:py-16">
        <div className="max-w-5xl mx-auto">
          <SectionTitle
            title="製品のご紹介"
            subtitle="自社開発の試験機器と高品質な表面処理剤で、お客様の品質向上をサポート"
            centered
          />

          <div className="mt-10 space-y-20 md:space-y-24">
            {products.map((p) => (
              <section
                key={p.id}
                id={p.id}
                ref={(el) => {
                  sectionRefs.current[p.id] = el;
                }}
                className="scroll-mt-[116px] md:scroll-mt-[132px]"
              >
                {/* Section header */}
                <div className="mb-6">
                  <span className="inline-block px-2.5 py-1 rounded-full bg-accent-100 text-accent-800 text-xs font-medium mb-2">
                    {p.category}
                  </span>
                  <div className="flex items-center gap-3 flex-wrap">
                    <h2 className="text-xl md:text-2xl font-bold text-foreground-900">
                      {p.name}
                    </h2>
                  </div>
                </div>

                {/* Product body: image + info */}
                <div className="flex flex-col lg:flex-row gap-8">
                  {/* Image */}
                  <div className="lg:w-[420px] shrink-0">
                    <a
                      href={p.pdf}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block relative aspect-[4/3] rounded-xl overflow-hidden border border-background-200/70 bg-background-100 cursor-pointer group"
                    >
                      <img
                        src={p.image}
                        alt={p.name}
                        className={`w-full h-full object-cover transition-all duration-500 ${
                          p.hoverImage ? 'group-hover:opacity-0' : 'group-hover:scale-105'
                        }`}
                      />
                      {p.badge && (
                        <span className="absolute top-3 right-3 px-3 py-1.5 rounded-md bg-foreground-800/90 text-background-50 text-xs font-bold z-10">
                          {p.badge}
                        </span>
                      )}
                      {p.hoverImage && (
                        <>
                          <img
                            src={p.hoverImage}
                            alt={`${p.name} - ドアオープン`}
                            className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                          />
                          <span className="absolute bottom-2 right-2 px-2 py-1 rounded-md bg-black/40 text-white text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                            ドアオープン
                          </span>
                        </>
                      )}
                    </a>
                  </div>

                  {/* Info */}
                  <div className="flex-1 space-y-5">
                    <p className="text-sm text-foreground-600 leading-relaxed">{p.desc}</p>

                    {/* Specs */}
                    <div>
                      <h4 className="text-sm font-bold text-foreground-900 mb-2 flex items-center gap-1.5">
                        <i className="ri-list-check text-primary-500" />
                        主な仕様
                      </h4>
                      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                        {p.specs.map((spec, si) => (
                          <li
                            key={si}
                            className="flex items-start gap-1.5 text-sm text-foreground-600"
                          >
                            <i className="ri-check-line text-accent-500 mt-0.5 shrink-0" />
                            <span>{spec}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Sub PDFs */}
                    {p.subProducts && p.subProducts.length > 0 && (
                      <div>
                        <h4 className="text-sm font-bold text-foreground-900 mb-2 flex items-center gap-1.5">
                          <i className="ri-folder-download-line text-secondary-500" />
                          関連資料
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {p.subProducts.map((sp, si) => (
                            <a
                              key={si}
                              href={sp.pdf}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-background-100 border border-background-200/60 text-sm text-foreground-600 hover:border-secondary-400 hover:text-secondary-700 hover:bg-secondary-50/50 transition-all duration-200"
                            >
                              <i className="ri-file-pdf-line text-secondary-500" />
                              <span>{sp.name}</span>
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </section>
            ))}
          </div>

          {/* Catalog Section */}
          <section
            id="catalog"
            ref={(el) => {
              sectionRefs.current.catalog = el;
            }}
            className="mt-20 md:mt-24 scroll-mt-[116px] md:scroll-mt-[132px]"
          >
            <div className="mb-6">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-900">資料・カタログ一覧</h2>
              <p className="text-sm text-foreground-500 mt-1">製品カタログや技術資料をPDF形式でご覧いただけます</p>
            </div>

            {/* KG Series */}
            <div className="mb-8">
              <h3 className="flex items-center gap-2 text-base font-bold text-foreground-800 mb-3">
                <span className="w-1.5 h-5 rounded-full bg-primary-500 inline-block" />
                KGシリーズ関連
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
                {kgCatalogs.map((item, i) => (
                  <a
                    key={i}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-3.5 py-3 rounded-lg bg-background-50 border border-background-200/60 hover:border-secondary-400 hover:bg-secondary-50/50 transition-all duration-200 group"
                  >
                    <i className="ri-file-pdf-line text-secondary-500 text-base shrink-0" />
                    <span className="text-sm text-foreground-700 group-hover:text-secondary-700 transition-colors line-clamp-1">
                      {item.label}
                    </span>
                  </a>
                ))}
              </div>
            </div>

            {/* MS7500 Series */}
            <div className="mb-8">
              <h3 className="flex items-center gap-2 text-base font-bold text-foreground-800 mb-3">
                <span className="w-1.5 h-5 rounded-full bg-accent-500 inline-block" />
                MS7500・接触抵抗評価関連
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
                {msCatalogs.map((item, i) => (
                  <a
                    key={i}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-3.5 py-3 rounded-lg bg-background-50 border border-background-200/60 hover:border-secondary-400 hover:bg-secondary-50/50 transition-all duration-200 group"
                  >
                    <i className="ri-file-pdf-line text-secondary-500 text-base shrink-0" />
                    <span className="text-sm text-foreground-700 group-hover:text-secondary-700 transition-colors line-clamp-1">
                      {item.label}
                    </span>
                  </a>
                ))}
              </div>
            </div>

            {/* General */}
            <div>
              <h3 className="flex items-center gap-2 text-base font-bold text-foreground-800 mb-3">
                <span className="w-1.5 h-5 rounded-full bg-secondary-500 inline-block" />
                総合資料
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
                {generalCatalogs.map((item, i) => (
                  <a
                    key={i}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-3.5 py-3 rounded-lg bg-background-50 border border-background-200/60 hover:border-secondary-400 hover:bg-secondary-50/50 transition-all duration-200 group"
                  >
                    <i className="ri-file-pdf-line text-secondary-500 text-base shrink-0" />
                    <span className="text-sm text-foreground-700 group-hover:text-secondary-700 transition-colors line-clamp-1">
                      {item.label}
                    </span>
                  </a>
                ))}
              </div>
            </div>
          </section>
        </div>
      </div>
    </PageLayout>
  );
}